import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';

const api = axios.create({ baseURL: '/api/socialmedia', headers: { 'Content-Type': 'application/json' } });

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

type TabKey = 'generate' | 'scheduled' | 'history' | 'settings';

interface Account {
  id: number;
  ghl_account_id: string;
  platform: string;
  account_name: string;
  avatar_url: string;
  is_active: boolean;
  fetched_at: string | null;
}

interface Post {
  id: number;
  project_id: number;
  credential_id: number | null;
  content: string;
  original_content: string | null;
  content_source: string | null;
  source_reference: string | null;
  platforms: string[] | null;
  ghl_account_ids: string[] | null;
  media_urls: string[] | null;
  status: string;
  scheduled_at: string | null;
  published_at: string | null;
  ghl_post_id: string | null;
  error_message: string | null;
  ai_model: string | null;
  ai_tokens_used: number | null;
  created_at: string | null;
  updated_at: string | null;
}

interface ContentSource {
  icp_jobs: { job_id: number; created_at: string | null }[];
  facts_jobs: { job_id: number; created_at: string | null }[];
}

interface Credentials {
  id: number;
  project_id: number;
  api_key_masked: string;
  location_id: string;
  is_verified: boolean;
  created_at: string | null;
  updated_at: string | null;
}

const PLATFORM_ICONS: Record<string, string> = {
  facebook: 'f',
  linkedin: 'in',
  instagram: 'ig',
  twitter: 'X',
};

const PLATFORM_COLORS: Record<string, string> = {
  facebook: 'bg-blue-600',
  linkedin: 'bg-blue-700',
  instagram: 'bg-pink-600',
  twitter: 'bg-gray-900',
};

const PLATFORM_LIMITS: Record<string, number> = {
  facebook: 2000,
  linkedin: 3000,
  instagram: 2200,
  twitter: 280,
};

const TONE_OPTIONS = ['professional', 'casual', 'witty', 'inspirational'];

export default function SocialMediaPage() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  // Support both new URL structure (/projects/:projectId/agent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id');

  const [activeTab, setActiveTab] = useState<TabKey>('generate');

  // Settings state
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [locationId, setLocationId] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  // Generate state
  const [contentSource, setContentSource] = useState<'icp' | 'facts' | 'blog_url' | 'manual'>('manual');
  const [sourceRef, setSourceRef] = useState('');
  const [contentSources, setContentSources] = useState<ContentSource | null>(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [tone, setTone] = useState('professional');
  const [instructions, setInstructions] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [generatedPosts, setGeneratedPosts] = useState<Post[]>([]);
  const [editingPosts, setEditingPosts] = useState<Record<number, string>>({});

  // Publish state
  const [publishingId, setPublishingId] = useState<number | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [showScheduler, setShowScheduler] = useState<number | null>(null);

  // Posts state
  const [posts, setPosts] = useState<Post[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<string>('all');

  // ── Load data ─────────────────────────────────────────────────

  useEffect(() => {
    if (projectId) {
      loadCredentials();
      loadAccounts();
      loadContentSources();
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId && (activeTab === 'history' || activeTab === 'scheduled')) {
      loadPosts();
    }
  }, [projectId, activeTab, historyFilter]);

  const loadCredentials = async () => {
    try {
      const { data } = await api.get(`/credentials/${projectId}`);
      setCredentials(data.credentials);
    } catch { /* no credentials yet */ }
  };

  const loadAccounts = async () => {
    try {
      const { data } = await api.get(`/accounts/${projectId}`);
      setAccounts(data.accounts || []);
    } catch { /* no accounts */ }
  };

  const loadContentSources = async () => {
    try {
      const { data } = await api.get(`/content-sources/${projectId}`);
      setContentSources({ icp_jobs: data.icp_jobs, facts_jobs: data.facts_jobs });
    } catch { /* ignore */ }
  };

  const loadPosts = async () => {
    setPostsLoading(true);
    try {
      const statusParam = activeTab === 'scheduled' ? 'scheduled' :
        historyFilter !== 'all' ? historyFilter : undefined;
      const url = `/posts/${projectId}` + (statusParam ? `?status=${statusParam}` : '');
      const { data } = await api.get(url);
      setPosts(data.posts || []);
    } catch { /* ignore */ }
    setPostsLoading(false);
  };

  // ── Settings actions ──────────────────────────────────────────

  const testConnection = async () => {
    setTestLoading(true);
    setTestResult(null);
    setSettingsError('');
    try {
      const { data } = await api.post('/credentials/test', { api_key: apiKey, location_id: locationId });
      setTestResult(`Connected! Found ${data.account_count} account(s).`);
    } catch (e: any) {
      setSettingsError(e.response?.data?.detail || 'Connection failed');
    }
    setTestLoading(false);
  };

  const saveCredentials = async () => {
    setSettingsLoading(true);
    setSettingsError('');
    try {
      await api.post(`/credentials/${projectId}`, { api_key: apiKey, location_id: locationId });
      await loadCredentials();
      await loadAccounts();
      setApiKey('');
      setLocationId('');
      setTestResult(null);
    } catch (e: any) {
      setSettingsError(e.response?.data?.detail || 'Failed to save');
    }
    setSettingsLoading(false);
  };

  const deleteCredentials = async () => {
    if (!confirm('Delete GHL credentials and all cached accounts?')) return;
    try {
      await api.delete(`/credentials/${projectId}`);
      setCredentials(null);
      setAccounts([]);
    } catch { /* ignore */ }
  };

  const refreshAccounts = async () => {
    setSettingsLoading(true);
    try {
      const { data } = await api.post(`/accounts/${projectId}/refresh`);
      setAccounts(data.accounts || []);
    } catch (e: any) {
      setSettingsError(e.response?.data?.detail || 'Refresh failed');
    }
    setSettingsLoading(false);
  };

  // ── Generate actions ──────────────────────────────────────────

  const handleGenerate = async () => {
    if (!selectedPlatforms.length) { setGenerateError('Select at least one platform'); return; }
    setGenerating(true);
    setGenerateError('');
    setGeneratedPosts([]);
    try {
      const { data } = await api.post('/generate', {
        project_id: Number(projectId),
        content_source: contentSource,
        source_reference: sourceRef || null,
        platforms: selectedPlatforms,
        tone,
        instructions,
      });
      setGeneratedPosts(data.posts || []);
    } catch (e: any) {
      setGenerateError(e.response?.data?.detail || 'Generation failed');
    }
    setGenerating(false);
  };

  const handleEditPost = (postId: number, content: string) => {
    setEditingPosts(prev => ({ ...prev, [postId]: content }));
  };

  const saveEdit = async (postId: number) => {
    const content = editingPosts[postId];
    if (!content) return;
    try {
      const { data } = await api.put(`/post/${postId}`, { content });
      setGeneratedPosts(prev => prev.map(p => p.id === postId ? data.post : p));
      setEditingPosts(prev => { const n = { ...prev }; delete n[postId]; return n; });
    } catch { /* ignore */ }
  };

  const publishNow = async (postId: number) => {
    const accountIds = accounts.filter(a => a.is_active).map(a => a.ghl_account_id);
    if (!accountIds.length) { setGenerateError('No connected accounts'); return; }
    setPublishingId(postId);
    try {
      const { data } = await api.post('/publish', { post_id: postId, account_ids: accountIds });
      setGeneratedPosts(prev => prev.map(p => p.id === postId ? data.post : p));
      setPosts(prev => prev.map(p => p.id === postId ? data.post : p));
    } catch (e: any) {
      setGenerateError(e.response?.data?.detail || 'Publish failed');
    }
    setPublishingId(null);
  };

  const schedulePost = async (postId: number) => {
    if (!scheduleDate) return;
    const accountIds = accounts.filter(a => a.is_active).map(a => a.ghl_account_id);
    if (!accountIds.length) { setGenerateError('No connected accounts'); return; }
    setPublishingId(postId);
    try {
      const { data } = await api.post('/publish', {
        post_id: postId,
        account_ids: accountIds,
        schedule_at: new Date(scheduleDate).toISOString(),
      });
      setGeneratedPosts(prev => prev.map(p => p.id === postId ? data.post : p));
      setShowScheduler(null);
      setScheduleDate('');
    } catch (e: any) {
      setGenerateError(e.response?.data?.detail || 'Schedule failed');
    }
    setPublishingId(null);
  };

  const cancelSchedule = async (postId: number) => {
    try {
      const { data } = await api.post(`/post/${postId}/cancel-schedule`);
      setPosts(prev => prev.map(p => p.id === postId ? data.post : p));
    } catch { /* ignore */ }
  };

  const deletePost = async (postId: number) => {
    if (!confirm('Delete this post?')) return;
    try {
      await api.delete(`/post/${postId}`);
      setGeneratedPosts(prev => prev.filter(p => p.id !== postId));
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch { /* ignore */ }
  };

  const reusePost = (post: Post) => {
    setActiveTab('generate');
    setContentSource('manual');
    setSourceRef(post.content);
    setSelectedPlatforms(post.platforms || []);
  };

  const togglePlatform = (p: string) => {
    setSelectedPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  if (!projectId) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto mt-20 text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">No Project Selected</h2>
          <p className="text-gray-500">Select a project first to use the Social Media Post Generator.</p>
        </div>
      </Layout>
    );
  }

  // ── Render ────────────────────────────────────────────────────

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'generate', label: 'Generate' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'history', label: 'History' },
    { key: 'settings', label: 'Settings' },
  ];

  return (
    <Layout projectId={Number(projectId)}>
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Social Media Post Generator</h1>
          <p className="text-gray-500 mt-1">Generate and publish social media posts via GoHighLevel</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === t.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Settings Tab ─────────────────────────────────────── */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Existing credentials */}
            {credentials && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Connected</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    credentials.is_verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {credentials.is_verified ? 'Verified' : 'Not verified'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div><span className="text-gray-500">API Key:</span> <span className="font-mono">{credentials.api_key_masked}</span></div>
                  <div><span className="text-gray-500">Location ID:</span> <span className="font-mono">{credentials.location_id}</span></div>
                </div>
                <button onClick={deleteCredentials} className="text-sm text-red-600 hover:text-red-800">
                  Remove Credentials
                </button>
              </div>
            )}

            {/* New/Update credentials */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">
                {credentials ? 'Update GHL Credentials' : 'Connect GoHighLevel'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                    placeholder="eyJhbGciOiJSUzI1NiIs..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location ID</label>
                  <input
                    type="text"
                    value={locationId}
                    onChange={e => setLocationId(e.target.value)}
                    placeholder="abc123xyz..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>
              {settingsError && <p className="text-red-600 text-sm mb-3">{settingsError}</p>}
              {testResult && <p className="text-green-600 text-sm mb-3">{testResult}</p>}
              <div className="flex gap-3">
                <button
                  onClick={testConnection}
                  disabled={!apiKey || !locationId || testLoading}
                  className="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  {testLoading ? 'Testing...' : 'Test Connection'}
                </button>
                <button
                  onClick={saveCredentials}
                  disabled={!apiKey || !locationId || settingsLoading}
                  className="px-4 py-2 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                >
                  {settingsLoading ? 'Saving...' : 'Save Credentials'}
                </button>
              </div>
            </div>

            {/* Connected Accounts */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Connected Accounts ({accounts.length})</h3>
                <button
                  onClick={refreshAccounts}
                  disabled={settingsLoading || !credentials}
                  className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Refresh
                </button>
              </div>
              {accounts.length === 0 ? (
                <p className="text-gray-400 text-sm">No accounts found. Save credentials and refresh.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {accounts.map(a => (
                    <div key={a.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className={`w-8 h-8 rounded-full ${PLATFORM_COLORS[a.platform] || 'bg-gray-500'} flex items-center justify-center text-white text-xs font-bold`}>
                        {PLATFORM_ICONS[a.platform] || '?'}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{a.account_name || a.platform}</p>
                        <p className="text-xs text-gray-500 capitalize">{a.platform}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Generate Tab ─────────────────────────────────────── */}
        {activeTab === 'generate' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Content Source</h3>
              <div className="flex gap-3 mb-4">
                {(['icp', 'facts', 'blog_url', 'manual'] as const).map(src => (
                  <button
                    key={src}
                    onClick={() => { setContentSource(src); setSourceRef(''); }}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      contentSource === src
                        ? 'bg-brand-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {src === 'blog_url' ? 'Blog URL' : src === 'icp' ? 'ICP' : src === 'facts' ? 'Facts' : 'Manual'}
                  </button>
                ))}
              </div>

              {contentSource === 'icp' && (
                <select
                  value={sourceRef}
                  onChange={e => setSourceRef(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Select ICP job...</option>
                  {contentSources?.icp_jobs.map(j => (
                    <option key={j.job_id} value={String(j.job_id)}>
                      Job #{j.job_id} {j.created_at ? `— ${new Date(j.created_at).toLocaleDateString()}` : ''}
                    </option>
                  ))}
                </select>
              )}
              {contentSource === 'facts' && (
                <select
                  value={sourceRef}
                  onChange={e => setSourceRef(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Select Facts job...</option>
                  {contentSources?.facts_jobs.map(j => (
                    <option key={j.job_id} value={String(j.job_id)}>
                      Job #{j.job_id} {j.created_at ? `— ${new Date(j.created_at).toLocaleDateString()}` : ''}
                    </option>
                  ))}
                </select>
              )}
              {contentSource === 'blog_url' && (
                <input
                  type="url"
                  value={sourceRef}
                  onChange={e => setSourceRef(e.target.value)}
                  placeholder="https://example.com/blog/article-title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              )}
              {contentSource === 'manual' && (
                <textarea
                  value={sourceRef}
                  onChange={e => setSourceRef(e.target.value)}
                  placeholder="Paste your content here..."
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y"
                />
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Platforms</h3>
              <div className="flex flex-wrap gap-3 mb-6">
                {accounts.length > 0 ? (
                  [...new Set(accounts.map(a => a.platform))].map(platform => (
                    <button
                      key={platform}
                      onClick={() => togglePlatform(platform)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedPlatforms.includes(platform)
                          ? `${PLATFORM_COLORS[platform] || 'bg-gray-700'} text-white`
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span className="font-bold">{PLATFORM_ICONS[platform] || '?'}</span>
                      <span className="capitalize">{platform}</span>
                    </button>
                  ))
                ) : (
                  <p className="text-gray-400 text-sm">Connect accounts in Settings tab first</p>
                )}
              </div>

              <h3 className="font-semibold text-gray-900 mb-3">Tone</h3>
              <div className="flex gap-2 mb-6">
                {TONE_OPTIONS.map(t => (
                  <button
                    key={t}
                    onClick={() => setTone(t)}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors capitalize ${
                      tone === t ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <h3 className="font-semibold text-gray-900 mb-3">Additional Instructions</h3>
              <textarea
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
                placeholder="Any specific instructions for the AI..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y mb-4"
              />

              {generateError && <p className="text-red-600 text-sm mb-3">{generateError}</p>}

              <button
                onClick={handleGenerate}
                disabled={generating || !sourceRef || !selectedPlatforms.length}
                className="px-6 py-2.5 bg-brand-600 text-white font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {generating ? 'Generating...' : 'Generate Posts'}
              </button>
            </div>

            {/* Generated Posts */}
            {generatedPosts.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Generated Posts</h3>
                {generatedPosts.map(post => {
                  const platform = post.platforms?.[0] || 'unknown';
                  const limit = PLATFORM_LIMITS[platform] || 2000;
                  const isEditing = editingPosts[post.id] !== undefined;
                  const currentContent = isEditing ? editingPosts[post.id] : post.content;
                  const charCount = currentContent.length;

                  return (
                    <div key={post.id} className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded-full ${PLATFORM_COLORS[platform] || 'bg-gray-500'} flex items-center justify-center text-white text-xs font-bold`}>
                            {PLATFORM_ICONS[platform] || '?'}
                          </div>
                          <span className="font-medium text-gray-900 capitalize">{platform}</span>
                          <StatusBadge status={post.status} />
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          {post.ai_model && <span>{post.ai_model}</span>}
                          <span className={charCount > limit ? 'text-red-500 font-medium' : ''}>
                            {charCount}/{limit}
                          </span>
                        </div>
                      </div>

                      <textarea
                        value={currentContent}
                        onChange={e => handleEditPost(post.id, e.target.value)}
                        rows={5}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y mb-3"
                      />

                      <div className="flex items-center gap-2 flex-wrap">
                        {isEditing && (
                          <button onClick={() => saveEdit(post.id)} className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700">
                            Save Edit
                          </button>
                        )}
                        {post.status === 'draft' && (
                          <>
                            <button
                              onClick={() => publishNow(post.id)}
                              disabled={publishingId === post.id}
                              className="px-3 py-1.5 text-xs font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                            >
                              {publishingId === post.id ? 'Publishing...' : 'Publish Now'}
                            </button>
                            <button
                              onClick={() => setShowScheduler(showScheduler === post.id ? null : post.id)}
                              className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                              Schedule
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => deletePost(post.id)}
                          className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          Delete
                        </button>
                      </div>

                      {showScheduler === post.id && (
                        <div className="mt-3 flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                          <input
                            type="datetime-local"
                            value={scheduleDate}
                            onChange={e => setScheduleDate(e.target.value)}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                          />
                          <button
                            onClick={() => schedulePost(post.id)}
                            disabled={!scheduleDate || publishingId === post.id}
                            className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                          >
                            Confirm Schedule
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Scheduled Tab ────────────────────────────────────── */}
        {activeTab === 'scheduled' && (
          <div className="space-y-4">
            {postsLoading ? (
              <div className="text-center py-12 text-gray-400">Loading...</div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12 text-gray-400">No scheduled posts</div>
            ) : (
              posts.map(post => (
                <div key={post.id} className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {(post.platforms || []).map(p => (
                        <div key={p} className={`w-6 h-6 rounded-full ${PLATFORM_COLORS[p] || 'bg-gray-500'} flex items-center justify-center text-white text-[10px] font-bold`}>
                          {PLATFORM_ICONS[p] || '?'}
                        </div>
                      ))}
                      <StatusBadge status={post.status} />
                    </div>
                    {post.scheduled_at && (
                      <span className="text-sm text-gray-500">
                        {new Date(post.scheduled_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap line-clamp-4">{post.content}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => cancelSchedule(post.id)}
                      className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => publishNow(post.id)}
                      disabled={publishingId === post.id}
                      className="px-3 py-1.5 text-xs font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                    >
                      Publish Now
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ── History Tab ──────────────────────────────────────── */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="flex gap-2 mb-4">
              {['all', 'published', 'failed', 'draft'].map(f => (
                <button
                  key={f}
                  onClick={() => setHistoryFilter(f)}
                  className={`px-3 py-1.5 text-sm rounded-lg capitalize transition-colors ${
                    historyFilter === f ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>

            {postsLoading ? (
              <div className="text-center py-12 text-gray-400">Loading...</div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12 text-gray-400">No posts found</div>
            ) : (
              posts.map(post => (
                <div key={post.id} className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {(post.platforms || []).map(p => (
                        <div key={p} className={`w-6 h-6 rounded-full ${PLATFORM_COLORS[p] || 'bg-gray-500'} flex items-center justify-center text-white text-[10px] font-bold`}>
                          {PLATFORM_ICONS[p] || '?'}
                        </div>
                      ))}
                      <StatusBadge status={post.status} />
                    </div>
                    <span className="text-xs text-gray-400">
                      {post.created_at ? new Date(post.created_at).toLocaleString() : ''}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap line-clamp-4">{post.content}</p>
                  {post.error_message && (
                    <p className="text-xs text-red-500 mb-2">Error: {post.error_message}</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => reusePost(post)}
                      className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Reuse
                    </button>
                    <button
                      onClick={() => deletePost(post.id)}
                      className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700',
    scheduled: 'bg-blue-100 text-blue-700',
    publishing: 'bg-yellow-100 text-yellow-700',
    published: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-500',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
}
